import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useDropdownData from "../hooks/useDropdownData";
import useCrearReservacion from "../hooks/useCrearReservacion";
import useFolio from "../hooks/useFolio";
import {
  esDiaValido,
  urlApi,
} from "../helpers/functionHelpers";
import { useAlert } from "../context/AlertContext";
import useEmpresaStore from "../context/empresaStore";
import ProbiomedExtra from "./ui/SelectorProbiomed";
import FileUploadButton from "./FileUploadButton";

import "../styles/NuevaReservacion.css";
import "../styles/FileUploadButton.css";
import logo from "../utils/sanfer-mx.png";

const NuevaReservacionMultiple = () => {
  const navigate = useNavigate();
  
  // Estado para datos del proveedor
  const [datosProveedor, setDatosProveedor] = useState({
    nombrePrv: "",
    correoPrv: "",
    telefonoPrv: "",
    compania: "",
    fechaRsv: "",
    almacen_id: "",
    folio: "",
  });
  
  // Estado para las OCs (array dinámico)
  const [ocs, setOcs] = useState([
    {
      id: 1,
      numeroOC: "",
      numFac: "",
      nmInsumo: "",
      cantidad: "",
      numeroCntdr: "",
      pesoOcantidad: "",
      numeroPallets: "",
      undmdas_id: "",
      containers_id: "",
      conditions_id: "",
      comentariosPrv: "",
      documentos: {
        oc: null,
        factura: null,
        certificado: null,
        otros: null
      }
    }
  ]);
  
  const [probiomedInterna, setProbiomedInterna] = useState('');
  const [materialSanfer, setMaterialSanfer] = useState('');
  const [proximoIdOC, setProximoIdOC] = useState(2);

  const { setEmpresaId, empresa_id } = useEmpresaStore();
  const [ubicaciones, setUbicaciones] = useState([]);

  // Custom Hooks para los dropdowns
  const { data: unidades, loading: loadingUnidades, error: errorUnidades } = 
    useDropdownData(`${urlApi}/api/unidades`, setDatosProveedor, "undmdas_id", true);
  
  const { data: contenedores, loading: loadingContenedores, error: errorContenedores } = 
    useDropdownData(`${urlApi}/api/contenedores`, setDatosProveedor, "containers_id");
  
  const { data: condiciones, loading: loadingCondiciones, error: errorCondiciones } = 
    useDropdownData(`${urlApi}/api/condiciones`, setDatosProveedor, "conditions_id");

  const { data: empresas, loading: loadingEmpresas, error: errorEmrpesas } = 
    useDropdownData(`${urlApi}/api/empresas`, setDatosProveedor, "empresas_id");

  const { data: ubicacionsProbiomed, loading: loadingUbicacionsProbiomed } = 
    useDropdownData(`${urlApi}/api/ubicacions`, setProbiomedInterna, "probiomedInterna", true);

  // Cargar ubicaciones cuando cambia la empresa
  useEffect(() => {
    if (!empresa_id) {
      setUbicaciones([]);
      setDatosProveedor(prev => ({ ...prev, almacen_id: "" }));
      return;
    }
    const token = localStorage.getItem('auth_token');
    fetch(`${urlApi}/api/ubicacions?empresa_id=${empresa_id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        setUbicaciones(Array.isArray(data) ? data : []);
        setDatosProveedor(prev => ({ ...prev, almacen_id: "" }));
      })
      .catch(() => setUbicaciones([]));
  }, [empresa_id]);

  // Obtener folio
  const { loading: loadingFolio, error: errorFolio, folioActual } = useFolio(setDatosProveedor, "folio");

  // Hook para crear reservación
  const { crearReservacion, loadingReservacion, errorReservacion } =
    useCrearReservacion(`${urlApi}/api/reservacion-multiple`);

  const { mostrarAlerta } = useAlert();

  // Manejar cambios en datos del proveedor
  const handleChangeProveedor = (e) => {
    const { name, value } = e.target;

    if (name === "empresas_id") {
      setEmpresaId(Number(value));
    }

    if (name === "fechaRsv") {
      const fechaSeleccionada = new Date(value);
      if (isNaN(fechaSeleccionada)) {
        mostrarAlerta("Fecha inválida", "error");
        return;
      }
    }

    setDatosProveedor({ ...datosProveedor, [name]: value });
  };

  // Manejar cambios en OCs individuales
  const handleChangeOC = (e, idOC) => {
    const { name, value } = e.target;
    setOcs(ocs.map(oc => 
      oc.id === idOC ? { ...oc, [name]: value } : oc
    ));
  };

  // Agregar nueva OC
  const handleAgregarOC = () => {
    const nuevoIdOC = proximoIdOC;
    setOcs([...ocs, {
      id: nuevoIdOC,
      numeroOC: "",
      numFac: "",
      nmInsumo: "",
      cantidad: "",
      numeroCntdr: "",
      pesoOcantidad: "",
      numeroPallets: "",
      undmdas_id: "",
      containers_id: "",
      conditions_id: "",
      comentariosPrv: "",
      documentos: {
        oc: null,
        factura: null,
        certificado: null,
        otros: null
      }
    }]);
    setProximoIdOC(nuevoIdOC + 1);
    mostrarAlerta(`OC #${nuevoIdOC} agregada`, "info");
  };

  // Eliminar OC
  const handleEliminarOC = (idOC) => {
    if (ocs.length === 1) {
      mostrarAlerta("Debe haber al menos una OC", "error");
      return;
    }
    setOcs(ocs.filter(oc => oc.id !== idOC));
    mostrarAlerta("OC eliminada", "info");
  };

  // Manejar actualización de documentos
  const handleDocumentoUploadado = (idOC, tipoDocumento, resultado) => {
    setOcs(ocs.map(oc => 
      oc.id === idOC 
        ? { ...oc, documentos: { ...oc.documentos, [tipoDocumento]: resultado } }
        : oc
    ));
  };

  // Validar formulario
  const validarFormulario = () => {
    if (!datosProveedor.nombrePrv || !datosProveedor.correoPrv || !datosProveedor.telefonoPrv ||
        !datosProveedor.compania || !datosProveedor.fechaRsv || !datosProveedor.almacen_id ||
        !empresa_id) {
      mostrarAlerta("Por favor, completa todos los datos del proveedor", "error");
      return false;
    }

    if (ocs.length === 0) {
      mostrarAlerta("Debe haber al menos una OC", "error");
      return false;
    }

    for (let index = 0; index < ocs.length; index++) {
      const oc = ocs[index];
      
      // Campos obligatorios en TODAS las OCs
      if (!oc.numeroOC || !oc.numFac || !oc.nmInsumo) {
        mostrarAlerta(`OC #${index + 1}: Por favor completa Número de OC, Factura e Insumo`, "error");
        return false;
      }

      // Campos detallados SOLO en la primera OC (index === 0)
      if (index === 0) {
        if (!oc.cantidad || !oc.numeroCntdr || !oc.pesoOcantidad || !oc.numeroPallets ||
            !oc.undmdas_id || !oc.containers_id || !oc.conditions_id) {
          mostrarAlerta(`OC #${index + 1}: Por favor completa todos los campos detallados`, "error");
          return false;
        }
      }

      // Documentos obligatorios en TODAS las OCs
      if (!oc.documentos.oc) {
        mostrarAlerta(`OC #${index + 1}: Falta adjuntar Orden de Compra (Obligatorio)`, "error");
        return false;
      }
    }

    const [year, month, day] = datosProveedor.fechaRsv.split("-");
    const fechaSeleccionada = new Date(year, month - 1, day);

    if (!esDiaValido(fechaSeleccionada)) {
      mostrarAlerta("No puedes seleccionar fines de semana, el día actual o días pasados.", "error");
      return false;
    }

    return true;
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) {
      return;
    }

    const extras = {
      ...(empresa_id === 3 && { materialSanfer }),
      ...(empresa_id === 4 && { probiomedInterna }),
    };

    const dataToSend = {
      folio: datosProveedor.folio || folioActual,
      nombrePrv: datosProveedor.nombrePrv,
      correoPrv: datosProveedor.correoPrv,
      telefonoPrv: datosProveedor.telefonoPrv,
      compania: datosProveedor.compania,
      fechaRsv: datosProveedor.fechaRsv,
      almacen_id: empresa_id === 4 ? probiomedInterna : datosProveedor.almacen_id,
      empresas_id: empresa_id,
      ocs: ocs.map((oc, index) => {
        // Base de datos para todas las OCs
        const ocData = {
          numeroOC: oc.numeroOC || "",
          numFac: oc.numFac || "",
          nmInsumo: oc.nmInsumo || "",
        };

        // Solo agregar campos detallados en la primera OC (index === 0)
        if (index === 0) {
          ocData.cantidad = oc.cantidad || "";
          ocData.numeroCntdr = oc.numeroCntdr || "";
          ocData.pesoOcantidad = oc.pesoOcantidad || "";
          ocData.numeroPallets = oc.numeroPallets || "";
          // Para IDs, usar '1' como valor por defecto si está vacío
          ocData.undmdas_id = oc.undmdas_id || "1";
          ocData.containers_id = oc.containers_id || "1";
          ocData.conditions_id = oc.conditions_id || "1";
          ocData.comentariosPrv = oc.comentariosPrv || "";
        }
        // Para OCs agregadas (index > 0), NO incluir los campos detallados
        // El backend usará valores por defecto con destructuring

        return ocData;
      }),
      extras
    };

    const resultado = await crearReservacion(dataToSend);
    if (resultado) {
      navigate(`/resumen/${dataToSend.folio}`);
    } else {
      mostrarAlerta("Hubo un problema al crear la reservación.", "error");
    }
  };

  return (
    <div className="reservation-container">
      <header className="reservation-header">
        <img src={logo} alt="Sanfer Logo" className="logo" />
        <h1>Solicitud de Reservación (Múltiples OCs)</h1>
        <div className="form-group">
          <label htmlFor="codigo">Código:</label>
          {loadingFolio ? (
            <p>Cargando folio...</p>
          ) : errorFolio ? (
            <p>Error: {errorFolio}</p>
          ) : (
            <input
              type="text"
              id="codigo"
              name="codigo"
              value={datosProveedor.folio || folioActual || ""}
              disabled
            />
          )}
        </div>
      </header>

      <form className="reservation-form" onSubmit={handleSubmit}>
        {/* SECCIÓN DATOS DEL PROVEEDOR */}
        <section className="form-section">
          <h2>Seleccione Empresa</h2>
          <div className="form-group">
            <label htmlFor="empresas">Seleccione la empresa:</label>
            {loadingEmpresas ? (
              <p>Cargando empresas...</p>
            ) : errorEmrpesas ? (
              <p>Error: {errorEmrpesas}</p>
            ) : (
              <select
                name="empresas_id"
                value={empresa_id || ''}
                onChange={handleChangeProveedor}
              >
                <option value="" disabled>-- Seleccione la empresa --</option>
                {empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.Empresa}
                  </option>
                ))}
              </select>
            )}
          </div>

          {empresa_id === 4 && !loadingUbicacionsProbiomed && (
            <ProbiomedExtra
              value={probiomedInterna}
              onChange={(e) => setProbiomedInterna(e.target.value)}
              options={ubicacionsProbiomed}
            />
          )}

          {empresa_id === 3 && (
            <div className="form-group">
              <label htmlFor="materialSanfer">Código de material de Sanfer:</label>
              <input
                type="text"
                id="materialSanfer"
                name="materialSanfer"
                value={materialSanfer}
                onChange={(e) => setMaterialSanfer(e.target.value)}
                placeholder="Código de material de Sanfer"
              />
            </div>
          )}
        </section>

        {/* DATOS DEL PROVEEDOR */}
        <section className="form-section">
          <h2>Datos del Proveedor</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nombre">Nombre:</label>
              <input
                type="text"
                id="nombre"
                name="nombrePrv"
                value={datosProveedor.nombrePrv}
                onChange={handleChangeProveedor}
                placeholder="Nombre del proveedor"
              />
            </div>
            <div className="form-group">
              <label htmlFor="telefono">Teléfono:</label>
              <input
                type="text"
                id="telefono"
                name="telefonoPrv"
                value={datosProveedor.telefonoPrv}
                onChange={handleChangeProveedor}
                placeholder="Teléfono"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="correo">Correo:</label>
              <input
                type="email"
                id="correo"
                name="correoPrv"
                value={datosProveedor.correoPrv}
                onChange={handleChangeProveedor}
                placeholder="Correo electrónico"
              />
            </div>
            <div className="form-group">
              <label htmlFor="compania">Compañía:</label>
              <input
                type="text"
                id="compania"
                name="compania"
                value={datosProveedor.compania}
                onChange={handleChangeProveedor}
                placeholder="Compañía"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fechaReserva">Fecha de reserva:</label>
              <input
                type="date"
                id="fechaReserva"
                name="fechaRsv"
                value={datosProveedor.fechaRsv}
                onChange={handleChangeProveedor}
              />
              <p className="note">Recuerda: NO puedes elegir fechas pasadas y mínimo 2 días de anticipación</p>
            </div>
            <div className="form-group">
              <label htmlFor="almacen">Almacén:</label>
              <select
                id="almacen"
                name="almacen_id"
                value={datosProveedor.almacen_id}
                onChange={handleChangeProveedor}
              >
                <option value="">-- Selecciona un almacén --</option>
                {ubicaciones.map(ubi => (
                  <option key={ubi.id} value={ubi.id}>
                    {ubi.descripcion}
                  </option>
                ))}
              </select>
              {!empresa_id && (
                <p className="note" style={{ color: '#999' }}>Selecciona primero una empresa</p>
              )}
            </div>
          </div>
        </section>

        {/* SECCIÓN OCs MÚLTIPLES */}
        <section className="form-section">
          <h2>Órdenes de Compra (OCs)</h2>
          <p className="info-text">Puedes agregar múltiples OCs para esta reservación</p>

          {ocs.map((oc, index) => (
            <div key={oc.id} className="oc-card" style={{ marginBottom: "30px", padding: "20px", border: "1px solid #ddd", borderRadius: "8px", background: index > 0 ? "#f9f9f9" : "white" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                <h3>OC #{index + 1}</h3>
                {ocs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleEliminarOC(oc.id)}
                    className="btn-eliminar-oc"
                    style={{ background: "#ff6b6b", color: "white", border: "none", padding: "8px 15px", borderRadius: "4px", cursor: "pointer" }}
                  >
                    Eliminar
                  </button>
                )}
              </div>

              {/* CAMPOS TEMPRALES - Se muestran en todas las OCs */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor={`numeroOC-${oc.id}`}>Número de OC:</label>
                  <input
                    type="text"
                    id={`numeroOC-${oc.id}`}
                    name="numeroOC"
                    value={oc.numeroOC}
                    onChange={(e) => handleChangeOC(e, oc.id)}
                    placeholder="Ej: OC-12345"
                  />
                  <FileUploadButton 
                    folio={datosProveedor.folio || folioActual || ""}
                    tipoDocumento="oc"
                    label="Adjuntar OC"
                    isRequired={true}
                    numeroOC={oc.numeroOC}
                    onUploadSuccess={(result) => handleDocumentoUploadado(oc.id, "oc", result)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`numFac-${oc.id}`}>Número de Factura:</label>
                  <input
                    type="text"
                    id={`numFac-${oc.id}`}
                    name="numFac"
                    value={oc.numFac}
                    onChange={(e) => handleChangeOC(e, oc.id)}
                    placeholder="Ej: FAC-12345"
                  />
                  <FileUploadButton 
                    folio={datosProveedor.folio || folioActual || ""}
                    tipoDocumento="factura"
                    label="Adjuntar Factura (Opcional)"
                    isRequired={false}
                    numFac={oc.numFac}
                    onUploadSuccess={(result) => handleDocumentoUploadado(oc.id, "factura", result)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor={`nmInsumo-${oc.id}`}>Nombre del Insumo:</label>
                  <input
                    type="text"
                    id={`nmInsumo-${oc.id}`}
                    name="nmInsumo"
                    value={oc.nmInsumo}
                    onChange={(e) => handleChangeOC(e, oc.id)}
                    placeholder="Ej: Materia Prima X"
                  />
                  <FileUploadButton 
                    folio={datosProveedor.folio || folioActual || ""}
                    tipoDocumento="certificado"
                    label="Adjuntar Certificado (Opcional)"
                    isRequired={false}
                    numeroOC={oc.numeroOC}
                    nmInsumo={oc.nmInsumo}
                    onUploadSuccess={(result) => handleDocumentoUploadado(oc.id, "certificado", result)}
                  />
                </div>
              </div>

              {/* CAMPOS DETALLADOS - Solo en la primera OC (index === 0) */}
              {index === 0 && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor={`cantidad-${oc.id}`}>Cantidad:</label>
                      <input
                        type="number"
                        id={`cantidad-${oc.id}`}
                        name="cantidad"
                        value={oc.cantidad}
                        onChange={(e) => handleChangeOC(e, oc.id)}
                        placeholder="Ej: 100"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`undmdas-${oc.id}`}>Unidad de medida:</label>
                      {loadingUnidades ? (
                        <p>Cargando...</p>
                      ) : (
                        <select
                          id={`undmdas-${oc.id}`}
                          name="undmdas_id"
                          value={oc.undmdas_id}
                          onChange={(e) => handleChangeOC(e, oc.id)}
                        >
                          <option value="">-- Seleccionar --</option>
                          {unidades.map((u) => (
                            <option key={u.id} value={u.id}>{u.descripcion}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor={`containers-${oc.id}`}>Contenedor:</label>
                      {loadingContenedores ? (
                        <p>Cargando...</p>
                      ) : (
                        <select
                          id={`containers-${oc.id}`}
                          name="containers_id"
                          value={oc.containers_id}
                          onChange={(e) => handleChangeOC(e, oc.id)}
                        >
                          <option value="">-- Seleccionar --</option>
                          {contenedores.map((c) => (
                            <option key={c.id} value={c.id}>{c.descripcion}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor={`numeroCntdr-${oc.id}`}>Número de Contenedores:</label>
                      <input
                        type="number"
                        id={`numeroCntdr-${oc.id}`}
                        name="numeroCntdr"
                        value={oc.numeroCntdr}
                        onChange={(e) => handleChangeOC(e, oc.id)}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor={`pesoOcantidad-${oc.id}`}>Peso por contenedor:</label>
                      <input
                        type="number"
                        id={`pesoOcantidad-${oc.id}`}
                        name="pesoOcantidad"
                        value={oc.pesoOcantidad}
                        onChange={(e) => handleChangeOC(e, oc.id)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`numeroPallets-${oc.id}`}>Número de Pallets:</label>
                      <input
                        type="number"
                        id={`numeroPallets-${oc.id}`}
                        name="numeroPallets"
                        value={oc.numeroPallets}
                        onChange={(e) => handleChangeOC(e, oc.id)}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor={`conditions-${oc.id}`}>Condiciones de almacenamiento:</label>
                      {loadingCondiciones ? (
                        <p>Cargando...</p>
                      ) : (
                        <select
                          id={`conditions-${oc.id}`}
                          name="conditions_id"
                          value={oc.conditions_id}
                          onChange={(e) => handleChangeOC(e, oc.id)}
                        >
                          <option value="">-- Seleccionar --</option>
                          {condiciones.map((cond) => (
                            <option key={cond.id} value={cond.id}>{cond.condicion}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor={`comentarios-${oc.id}`}>Comentarios:</label>
                    <textarea
                      id={`comentarios-${oc.id}`}
                      name="comentariosPrv"
                      value={oc.comentariosPrv}
                      onChange={(e) => handleChangeOC(e, oc.id)}
                      placeholder="Comentarios adicionales (opcional)"
                      rows="3"
                    />
                    <FileUploadButton 
                      folio={datosProveedor.folio || folioActual || ""}
                      tipoDocumento="otros"
                      label="Adjuntar Otros Documentos"
                      isRequired={false}
                      onUploadSuccess={(result) => handleDocumentoUploadado(oc.id, "otros", result)}
                    />
                  </div>
                </>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={handleAgregarOC}
            className="btn-agregar-oc"
            style={{ background: "#2196F3", color: "white", border: "none", padding: "10px 20px", borderRadius: "4px", cursor: "pointer", fontSize: "1rem", fontWeight: "bold", marginBottom: "20px" }}
          >
            + Agregar otra OC
          </button>
        </section>

        {/* BOTÓN ENVÍO */}
        <section className="form-section">
          <button
            type="submit"
            disabled={loadingReservacion}
            className="submit-button"
            style={{ background: "#4CAF50", color: "white", padding: "15px 40px", fontSize: "1.1rem", fontWeight: "bold", border: "none", borderRadius: "4px", cursor: "pointer", width: "100%" }}
          >
            {loadingReservacion ? "Creando reservación..." : `Crear reservación con ${ocs.length} OC${ocs.length > 1 ? 's' : ''}`}
          </button>
          {errorReservacion && (
            <p style={{ color: "red", marginTop: "10px" }}>Error: {errorReservacion}</p>
          )}
        </section>
      </form>
    </div>
  );
};

export default NuevaReservacionMultiple;
